import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import Sidebar from './Sidebar';
import TicketList from '../tickets/TicketList';
import BoardView from '../tickets/BoardView';
import CreateTicket from '../tickets/CreateTicket';
import TicketDetail from '../tickets/TicketDetail';
import PersistentTabBar, { PersistentTabBarRef } from '../tickets/PersistentTabBar';
import UserManagement from '../users/UserManagement';
import AuditTrail from '../users/AuditTrail';
import Insights from './Insights';
import CustomerManagement from '../customers/CustomerManagement';
import MyAccount from '../agent/MyAccount';
import DeviceManagement from '../devices/DeviceManagement';
import CompanyManagement from '../companies/CompanyManagement';
import FileDashboard from './FileDashboard';
import CompanyMatchNotification from '../common/CompanyMatchNotification';
import ServiceList from '../service/ServiceList';

import { Ticket, User } from '../../types';
import apiService from '../../services/api';
import socketService from '../../services/socket';
import soundService from '../../services/soundService';
import backgroundEmailService from '../../services/backgroundEmailService';
import { isTicketClosed } from '../../utils/statusUtils';

type ViewType = 'board' | 'tickets' | 'my-tickets' | 'my-open-tickets' | 'all-open-tickets' | 'unassigned' | 'resolved' | 'service-tickets' | 'create' | 'detail' | 'users' | 'audit' | 'insights' | 'customers' | 'devices' | 'companies' | 'files' | 'account';

const Dashboard: React.FC = () => {
  const { user, token, logout, updateUser } = useAuth();
  const { addNotification } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Debug log to confirm Dashboard component is loading
  console.log('🔥 DASHBOARD COMPONENT LOADED - User:', user);
  const [currentView, setCurrentView] = useState<ViewType>(user?.role === 'agent' ? 'board' : 'tickets');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoUpdating, setAutoUpdating] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [messageBlinkingTickets, setMessageBlinkingTickets] = useState<Set<string>>(new Set());
  const [companyMatchNotifications, setCompanyMatchNotifications] = useState<any[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [paginationData, setPaginationData] = useState<any>(null);

  // Filter state (for saving preferences)
  const [savedFilters, setSavedFilters] = useState({
    statusFilter: 'all',
    priorityFilter: 'all',
    searchTerm: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Preferences loading state
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Tab Manager state
  const [activeTabTicket, setActiveTabTicket] = useState<Ticket | null>(null);

  // Ref for PersistentTabBar to trigger manual refreshes
  const tabBarRef = useRef<PersistentTabBarRef>(null);

  // Ticket selection handler - defined early for use in useEffects
  const handleTicketSelect = useCallback((ticket: Ticket) => {
    console.log('🎯 Dashboard: handleTicketSelect called with ticket:', ticket.id, ticket.ticketNumber);
    // Set as active tab ticket and switch to ticket detail view
    setActiveTabTicket(ticket);
    setSelectedTicket(ticket);
    setCurrentView('detail');
  }, []);

  // Clear selected customer/device when switching away from their respective views
  useEffect(() => {
    if (currentView !== 'customers' && selectedCustomer) {
      setSelectedCustomer(null);
    }
    if (currentView !== 'devices' && selectedDevice) {
      setSelectedDevice(null);
    }
  }, [currentView, selectedCustomer, selectedDevice]);

  // Initialize background email service
  useEffect(() => {
    console.log('🚀 Dashboard mounted - background email service will auto-start');
    
    // Cleanup function to stop service when component unmounts
    return () => {
      console.log('🛑 Dashboard unmounting - stopping background email service');
      backgroundEmailService.stop();
    };
  }, []);

  // Helper function to determine urgency level
  const getTicketUrgencyLevel = useCallback((ticket: Ticket): 'green' | 'yellow' | 'red' => {
    const elapsed = Math.floor((Date.now() - new Date(ticket.createdAt).getTime()) / 1000);
    const isAssigned = !!ticket.agentId;
    const isResolved = isTicketClosed(ticket.status);
    const isInProgress = ticket.status === 'in_progress';

    if (isResolved) {
      return 'green';
    } else if (isInProgress) {
      const elapsedMinutes = Math.floor(elapsed / 60);
      if (elapsedMinutes > 20) return 'red';
      if (elapsedMinutes > 10) return 'yellow';
      return 'green';
    } else if (isAssigned) {
      return 'green';
    } else if (elapsed > 120) {
      return 'red';
    } else if (elapsed > 60) {
      return 'yellow';
    } else {
      return 'green';
    }
  }, []);

  // Play sound based on urgency changes
  const playUrgencySound = useCallback(async (ticket: Ticket, isNewTicket: boolean = false) => {
    if (!soundEnabled) return;

    const urgencyLevel = getTicketUrgencyLevel(ticket);
    
    try {
      if (isNewTicket) {
        // For new tickets, play sound based on initial urgency
        switch (urgencyLevel) {
          case 'green':
            await soundService.playNewTicketSound();
            break;
          case 'yellow':
            await soundService.playYellowTicketSound();
            break;
          case 'red':
            await soundService.playRedTicketSound();
            break;
        }
      } else {
        // For updates, play update sound
        await soundService.playTicketUpdateSound();
      }
    } catch (error) {
      console.warn('Error playing sound:', error);
    }
  }, [soundEnabled, getTicketUrgencyLevel]);

  // Check for urgency changes and play sounds
  const checkUrgencyChanges = useCallback((oldTickets: Ticket[], newTickets: Ticket[]) => {
    if (!soundEnabled || oldTickets.length === 0) return;

    newTickets.forEach(newTicket => {
      const oldTicket = oldTickets.find(t => t.id === newTicket.id);
      
      if (!oldTicket) {
        // This is a new ticket
        playUrgencySound(newTicket, true);
      } else {
        // Check if urgency level changed
        const oldUrgency = getTicketUrgencyLevel(oldTicket);
        const newUrgency = getTicketUrgencyLevel(newTicket);
        
        if (oldUrgency !== newUrgency && newUrgency !== 'green') {
          // Urgency increased (only play for yellow/red)
          playUrgencySound(newTicket, false);
        }
      }
    });
  }, [soundEnabled, playUrgencySound, getTicketUrgencyLevel]);

  // Handle customer message to start blue blinking
  const handleCustomerMessage = (ticketId: string) => {
    setMessageBlinkingTickets(prev => {
      const updated = new Set(prev);
      updated.add(ticketId);
      return updated;
    });
  };

  // Handle agent reply to stop blue blinking
  const handleAgentReply = (ticketId: string) => {
    setMessageBlinkingTickets(prev => {
      const updated = new Set(prev);
      updated.delete(ticketId);
      return updated;
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('🔥 DASHBOARD useEffect TRIGGERED - User role:', user?.role, 'User ID:', user?.id);
    
    const initializeDashboard = async () => {
      console.log('🔥 DASHBOARD initializeDashboard function started');
      loadTickets();
      
      try {
        // Connect to Socket.IO for real-time updates and wait for connection
        console.log('🚀 Dashboard: Connecting to Socket.IO...');
        await socketService.connect(token ?? undefined);
        console.log('✅ Dashboard: Socket.IO connected successfully');
        
        // Reduced delay for faster notification display - connection should be ready
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Join agent dashboard if user is an agent
        if (user?.role === 'agent' && user?.id) {
          const agentName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'Unknown Agent';
          console.log('🚀 DASHBOARD: Attempting to join agent dashboard');
          console.log('   - User ID:', user.id);
          console.log('   - User Role:', user.role);
          console.log('   - User Type:', user.userType);
          console.log('   - Agent Name:', agentName);
          console.log('   - Socket connected:', socketService.isSocketConnected());
          
          // Retry mechanism for agent dashboard join
          let retries = 3;
          const attemptJoin = async () => {
            while (retries > 0) {
              try {
                console.log(`🔄 DASHBOARD: Join attempt ${4 - retries}/3`);
                socketService.joinAgentDashboard(user.id, agentName);
                console.log('✅ DASHBOARD: Agent dashboard join call completed');
                break;
              } catch (error) {
                console.warn(`⚠️ DASHBOARD: Agent dashboard join failed, ${retries} retries left:`, error);
                retries--;
                if (retries > 0) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              }
            }
          };
          
          attemptJoin();
          
          // Log success confirmation
          console.log('🎯 DASHBOARD: Agent dashboard join attempt completed');
        } else {
          console.log('🔥 DASHBOARD: User is not an agent or missing ID');
          console.log('   - User Role:', user?.role);
          console.log('   - User Type:', user?.userType);
          console.log('   - User ID:', user?.id);
          console.log('   - Full User Object:', user);
        }
      } catch (error) {
        console.error('❌ Dashboard: Failed to connect to Socket.IO:', error);
        // Still continue with dashboard functionality even if socket fails
      }
    };

    initializeDashboard();
    
    // Handle browser/tab close to ensure proper disconnect
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Leave agent dashboard before disconnecting
      if (user?.role === 'agent' && user?.id) {
        const agentName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'Unknown Agent';
        socketService.leaveAgentDashboard(user.id, agentName);
      }
      socketService.disconnect();
    };

    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Listen for new tickets
    const handleNewTicket = (data: { ticket: any }) => {
      console.log('🎫 New ticket received via Socket.IO:', data.ticket);
      // Play new ticket sound immediately
      if (soundEnabled) {
        playUrgencySound(data.ticket, true);
      }
      // IMMEDIATE refresh on new ticket
      console.log('🚀 Dashboard: Immediate refresh triggered by new ticket');
      loadTickets(true); // Auto-update with current settings from refs
      
      // Double-check refresh after 1 second to ensure tickets are visible
      setTimeout(() => {
        console.log('🔄 Dashboard: Follow-up refresh after new ticket');
        loadTickets(true);
      }, 1000);
    };
    
    // Listen for ticket updates
    const handleTicketUpdate = (data: any) => {
      console.log('🔄 Ticket update received via Socket.IO:', data);
      loadTickets(true); // Auto-update with current settings from refs
    };

    // Listen for ticket deletions
    const handleTicketDelete = (data: { ticketId: string; deletedBy: string; deletedAt: string }) => {
      console.log('🗑️ Ticket deletion received via Socket.IO:', data);
      
      // If the currently selected ticket was deleted, go back to list
      if (selectedTicketRef.current && selectedTicketRef.current.id === data.ticketId) {
        setSelectedTicket(null);
        setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
      }
      
      loadTickets(true); // Auto-update with current settings from refs
    };

    // Listen for new messages
    const handleNewMessage = (data: { message: any }) => {
      console.log('📨 Dashboard received new message via Socket.IO:', data.message);
      
      // Check if this is a customer message
      if (data.message.sender?.userType === 'customer') {
        console.log('🔵 Customer message received, starting blue blink for ticket:', data.message.ticketId);
        handleCustomerMessage(data.message.ticketId);
      } else if (data.message.sender?.userType === 'agent') {
        console.log('👨‍💼 Agent message received, stopping blue blink for ticket:', data.message.ticketId);
        handleAgentReply(data.message.ticketId);
      }
      
      // Refresh tickets to update message timestamps
      loadTickets(true);
    };

    // Listen for company match suggestions
    const handleCompanyMatchSuggestion = (data: any) => {
      console.log('🏢 Company match suggestion received:', data);
      
      // Add to notifications if user has permission to assign companies
      if (user?.permissions?.includes('companies.assign')) {
        setCompanyMatchNotifications(prev => [...prev, {
          id: data.pendingMatchId,
          ticketId: data.ticketId,
          ticketNumber: data.ticketNumber,
          customerName: data.customerName,
          inputCompanyName: data.inputCompanyName,
          suggestedCompany: {
            id: data.suggestedCompany,
            name: data.suggestedCompany
          },
          confidence: data.confidence,
          message: data.message
        }]);
      }
    };

    // Listen for agent notifications
    const handleAgentNotification = (data: any) => {
      console.log('🔔 Agent notification received:', data);
      
      // Only show notifications for agents
      if (user?.userType === 'agent' || user?.role === 'agent') {
        addNotification({
          type: data.type,
          priority: data.priority,
          title: data.title,
          message: data.message,
          actionButton: data.actionButton,
          sound: data.sound,
          data: data.data
        });
      }
    };

    // Listen for ticket assignments
    const handleTicketAssignment = async (data: { ticketId: string; agentId: string; agentName?: string; assignedAt: string }) => {
      console.log('🎯 Ticket assignment received:', data);
      
      // If this agent was assigned the ticket, auto-open it in tabs
      if (user?.id === data.agentId && user?.role === 'agent') {
        console.log('✋ Ticket assigned to current agent, fetching ticket data for auto-open');
        
        try {
          // Fetch the full ticket data since backend only sends assignment info
          const ticket = await apiService.getTicketById(data.ticketId);
          if (ticket) {
            console.log('✅ Ticket data fetched, auto-opening in tab:', ticket);
            handleTicketSelect(ticket);
            
            // Play assignment sound
            if (soundEnabled) {
              soundService.playTicketUpdateSound();
            }
          }
        } catch (error) {
          console.error('❌ Error fetching ticket for auto-open:', error);
        }
        
        // Aggressively refresh tab bar to show new assigned ticket
        console.log('🚀 Dashboard: Aggressively refreshing tab bar after ticket assignment');
        
        // Immediate refresh (no delay)
        if (tabBarRef.current) {
          console.log('🚀 Dashboard: Immediate refresh for assignment');
          tabBarRef.current.refreshTabs();
        }
        
        // Additional refresh burst (every 100ms for 2 seconds) - faster and shorter
        for (let i = 0; i < 20; i++) {
          setTimeout(() => {
            if (tabBarRef.current) {
              console.log(`🚀 Dashboard: Burst refresh ${i + 1}/20 for assignment`);
              tabBarRef.current.refreshTabs();
            }
          }, i * 100);
        }
      }
      
            // Refresh tickets to show updated assignment
      loadTickets(true);
    };

    // Listen for ticket claims (manual assignments)
    const handleTicketClaimed = async (data: { ticketId: string; agent: { id: string; firstName: string; lastName: string }; claimedAt: string; handoffFromAI?: boolean; autoClaimed?: boolean }) => {
      console.log('🎯 Ticket claim received:', data);
      
      // If this agent claimed the ticket, auto-open it in tabs
      if (user?.id === data.agent.id && user?.role === 'agent') {
        console.log('✋ Ticket claimed by current agent, fetching ticket data for auto-open');
        
        try {
          // Fetch the full ticket data
          const ticket = await apiService.getTicketById(data.ticketId);
          if (ticket) {
            console.log('✅ Ticket data fetched, auto-opening in tab:', ticket);
            handleTicketSelect(ticket);
            
            // Play assignment sound
            if (soundEnabled) {
              soundService.playTicketUpdateSound();
            }
          }
        } catch (error) {
          console.error('❌ Error fetching ticket for auto-open:', error);
        }
        
        // Aggressively refresh tab bar to show new claimed ticket
        console.log('🚀 Dashboard: Aggressively refreshing tab bar after ticket claim');
        
        // Immediate refresh (no delay)
        if (tabBarRef.current) {
          console.log('🚀 Dashboard: Immediate refresh for claim');
          tabBarRef.current.refreshTabs();
        }
        
        // Additional refresh burst (every 100ms for 2 seconds) - faster and shorter
        for (let i = 0; i < 20; i++) {
          setTimeout(() => {
            if (tabBarRef.current) {
              console.log(`🚀 Dashboard: Burst refresh ${i + 1}/20 for claim`);
              tabBarRef.current.refreshTabs();
            }
          }, i * 100);
        }
      }
      
      // Refresh tickets to show updated assignment
      loadTickets(true);
    };
     
    socketService.on('new_ticket', handleNewTicket);
    socketService.on('ticket_updated', handleTicketUpdate);
    socketService.on('ticket_deleted', handleTicketDelete);
    socketService.on('new_message', handleNewMessage);
    socketService.on('company_match_suggestion', handleCompanyMatchSuggestion);
    socketService.on('ticket_assigned', handleTicketAssignment);
    socketService.on('ticket_claimed', handleTicketClaimed);
    socketService.on('agent_notification', handleAgentNotification);
    
    // Set up periodic auto-refresh as fallback
    // AGGRESSIVE: Use 5-second refresh for board view to ensure tickets stay visible
    let autoRefreshInterval: NodeJS.Timeout;
    
    const setupAutoRefresh = () => {
      // Clear existing interval if any
      if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
      }
      
      // Determine refresh interval based on current view
      // Board view gets aggressive 5-second refresh to prevent disappearing tickets
      const refreshInterval = currentViewRef.current === 'board' ? 5000 : 30000;
      console.log(`🔄 Setting up auto-refresh interval: ${refreshInterval / 1000} seconds for view: ${currentViewRef.current}`);
      
      autoRefreshInterval = setInterval(() => {
        console.log('🔄 Auto-refreshing ticket list from interval...');
        loadTickets(true); // Auto-update with current settings from refs
      }, refreshInterval);
    };
    
    // Initial setup
    setupAutoRefresh();
    
    // Check every 3 seconds if we need to adjust the interval (when switching between board and other views)
    const viewCheckInterval = setInterval(() => {
      // Recreate interval if view changed
      setupAutoRefresh();
    }, 3000);
    
    return () => {
      // Leave agent dashboard if user is an agent
      if (user?.role === 'agent' && user?.id) {
        const agentName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || 'Unknown Agent';
        socketService.leaveAgentDashboard(user.id, agentName);
      }
      
      // Remove event listener
      window.removeEventListener('beforeunload', handleBeforeUnload);
      socketService.off('new_ticket', handleNewTicket);
      socketService.off('ticket_updated', handleTicketUpdate);
      socketService.off('ticket_deleted', handleTicketDelete);
      socketService.off('new_message', handleNewMessage);
      socketService.off('company_match_suggestion', handleCompanyMatchSuggestion);
      socketService.off('ticket_assigned', handleTicketAssignment);
      socketService.off('ticket_claimed', handleTicketClaimed);
      socketService.off('agent_notification', handleAgentNotification);
      if (autoRefreshInterval) clearInterval(autoRefreshInterval);
      if (viewCheckInterval) clearInterval(viewCheckInterval);
      socketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle URL parameters to auto-open tickets
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const ticketId = searchParams.get('ticket');
    const action = searchParams.get('action');
    
    if (ticketId && user?.role === 'agent') {
      console.log('🎫 Dashboard: Auto-opening ticket from URL:', ticketId, 'Action:', action);
      
      const openTicketFromUrl = async () => {
        try {
          // First try to find the ticket in the current list
          let ticket = tickets.find(t => t.id === ticketId);
          
          // If not found, fetch from API
          if (!ticket) {
            console.log('📡 Fetching ticket from API for URL navigation:', ticketId);
            ticket = await apiService.getTicketById(ticketId);
          }
          
          if (ticket) {
            console.log('✅ Opening ticket from URL:', ticket.ticketNumber);
            handleTicketSelect(ticket);
            
            // Clear the URL parameters after successful navigation
            navigate('/agent', { replace: true });
          } else {
            console.error('❌ Could not find ticket for URL navigation:', ticketId);
          }
        } catch (error) {
          console.error('💥 Error opening ticket from URL:', error);
        }
      };
      
      // Only proceed if we have loaded tickets already
      if (tickets.length > 0 || loading === false) {
        openTicketFromUrl();
      }
    }
  }, [location.search, user?.role, tickets, loading, navigate, handleTicketSelect]);

  // Load user preferences from localStorage on component mount
  useEffect(() => {
    const loadPreferences = () => {
      const storageKey = 'ticket-list-preferences';
      const defaultPrefs = {
        itemsPerPage: 50,
        statusFilter: 'all',
        priorityFilter: 'all',
        searchTerm: '',
        sortBy: 'created_at',
        sortOrder: 'desc'
      };

      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          return {
            itemsPerPage: parsed.itemsPerPage || defaultPrefs.itemsPerPage,
            statusFilter: parsed.statusFilter || defaultPrefs.statusFilter,
            priorityFilter: parsed.priorityFilter || defaultPrefs.priorityFilter,
            searchTerm: parsed.searchTerm || defaultPrefs.searchTerm,
            sortBy: parsed.sortBy || defaultPrefs.sortBy,
            sortOrder: parsed.sortOrder || defaultPrefs.sortOrder,
          };
        }
      } catch (error) {
        console.error('Error loading ticket preferences:', error);
      }
      return defaultPrefs;
    };

    const preferences = loadPreferences();
    
    // Apply loaded preferences
    setItemsPerPage(preferences.itemsPerPage);
    setSavedFilters({
      statusFilter: preferences.statusFilter,
      priorityFilter: preferences.priorityFilter,
      searchTerm: preferences.searchTerm,
      sortBy: preferences.sortBy,
      sortOrder: preferences.sortOrder
    });
    
    // Update refs immediately to avoid race condition on initial load
    itemsPerPageRef.current = preferences.itemsPerPage;
    savedFiltersRef.current = {
      statusFilter: preferences.statusFilter,
      priorityFilter: preferences.priorityFilter,
      searchTerm: preferences.searchTerm,
      sortBy: preferences.sortBy,
      sortOrder: preferences.sortOrder
    };
    
    setPreferencesLoaded(true);
  }, []);

  // Use refs for stable values in auto-refresh
  const ticketsRef = useRef<Ticket[]>([]);
  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const savedFiltersRef = useRef(savedFilters);
  const selectedTicketRef = useRef(selectedTicket);
  const currentViewRef = useRef(currentView);
  const isLoadingTicketsRef = useRef(false);
  const lastKnownGoodTicketsRef = useRef<Ticket[]>([]); // Backup of last valid state
  const lastSuccessfulFetchRef = useRef<Date | null>(null);

  // Update refs when state changes
  useEffect(() => { ticketsRef.current = tickets; }, [tickets]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { itemsPerPageRef.current = itemsPerPage; }, [itemsPerPage]);
  useEffect(() => { savedFiltersRef.current = savedFilters; }, [savedFilters]);
  useEffect(() => { selectedTicketRef.current = selectedTicket; }, [selectedTicket]);
  useEffect(() => { currentViewRef.current = currentView; }, [currentView]);

  // Load tickets function - moved here to avoid "used before declaration" error
  const loadTickets = useCallback(async (isAutoUpdate = false, page?: number, limit?: number, filters?: any) => {
    // Prevent concurrent requests - skip if already loading
    if (isLoadingTicketsRef.current) {
      console.log('⏭️ Skipping loadTickets - already in progress');
      return;
    }
    
    try {
      isLoadingTicketsRef.current = true; // Mark as loading
      
      if (isAutoUpdate) {
        setAutoUpdating(true);
      } else {
        setLoading(true);
      }
      
      const oldTickets = [...ticketsRef.current];
      
      // Use current state values if parameters are not provided
      const actualPage = page ?? currentPageRef.current;
      const actualLimit = limit ?? itemsPerPageRef.current;
      const actualFilters = filters ?? savedFiltersRef.current;
      
      const ticketFilters: any = {
        page: actualPage,
        limit: actualLimit
      };

      // Add filters to API call if they're not defaults
      if (actualFilters.statusFilter && actualFilters.statusFilter !== 'all') {
        ticketFilters.status = actualFilters.statusFilter;
      }
      if (actualFilters.priorityFilter && actualFilters.priorityFilter !== 'all') {
        ticketFilters.priority = actualFilters.priorityFilter;
      }
      if (actualFilters.searchTerm) {
        ticketFilters.search = actualFilters.searchTerm;
      }

      console.log('📡 Dashboard: Fetching tickets with filters:', ticketFilters);
      const response = await apiService.getTickets(ticketFilters);
      console.log('📦 Dashboard: Received tickets:', response.tickets.length);
      
      // PROTECTION: Validate response before updating state
      // If we get an empty response but had tickets before, keep the old ones
      if (response.tickets.length === 0 && lastKnownGoodTicketsRef.current.length > 0 && isAutoUpdate) {
        console.warn('⚠️ Dashboard: Received empty ticket list during auto-update, keeping previous tickets');
        console.warn('⚠️ Last known good tickets:', lastKnownGoodTicketsRef.current.length);
        // Don't update - keep existing tickets
        return;
      }
      
      // Update state with new tickets
      setTickets(response.tickets);
      setPaginationData(response.pagination);
      setTotalItems(response.pagination.totalItems);
      setTotalPages(response.pagination.totalPages);
      setCurrentPage(response.pagination.currentPage);
      setLastUpdated(new Date());
      
      // Store as last known good state if we got tickets
      if (response.tickets.length > 0) {
        lastKnownGoodTicketsRef.current = [...response.tickets];
        lastSuccessfulFetchRef.current = new Date();
        console.log('✅ Dashboard: Stored last known good state:', response.tickets.length, 'tickets');
      }
      
      // Check for urgency changes and play sounds
      if (isAutoUpdate) {
        checkUrgencyChanges(oldTickets, response.tickets);
      }
      
      // Update selected ticket if it exists in the refreshed list
      const currentSelectedTicket = selectedTicketRef.current;
      if (currentSelectedTicket) {
        const updatedTicket = response.tickets.find(t => t.id === currentSelectedTicket.id);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
      }
    } catch (error) {
      console.error('❌ Dashboard: Error loading tickets:', error);
      
      // On error during auto-update, restore last known good state
      if (isAutoUpdate && lastKnownGoodTicketsRef.current.length > 0) {
        console.warn('🔄 Dashboard: Restoring last known good state after error');
        setTickets([...lastKnownGoodTicketsRef.current]);
      }
    } finally {
      setLoading(false);
      setAutoUpdating(false);
      isLoadingTicketsRef.current = false; // Clear loading flag
    }
  }, [checkUrgencyChanges]);

  // Load tickets when preferences are loaded
  useEffect(() => {
    if (preferencesLoaded) {
      loadTickets();
    }
  }, [preferencesLoaded, loadTickets]);

  // Ref for debounce timeout
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced function to save preferences to localStorage
  const savePreferencesDebounced = useCallback((preferences: any) => {
    const saveToStorage = () => {
      try {
        const storageKey = 'ticket-list-preferences';
        localStorage.setItem(storageKey, JSON.stringify(preferences));
        console.log('✅ Ticket preferences saved to localStorage');
      } catch (error) {
        console.error('Error saving ticket preferences:', error);
      }
    };
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout
    saveTimeoutRef.current = setTimeout(saveToStorage, 1000);
  }, []);

  // Save preferences when they change
  useEffect(() => {
    if (!preferencesLoaded) return;

    const preferences = {
      itemsPerPage,
      sortBy: savedFilters.sortBy,
      sortOrder: savedFilters.sortOrder,
      statusFilter: savedFilters.statusFilter,
      priorityFilter: savedFilters.priorityFilter,
      searchTerm: savedFilters.searchTerm
    };

    savePreferencesDebounced(preferences);
  }, [itemsPerPage, savedFilters, preferencesLoaded, savePreferencesDebounced]);



  const handleTabClick = (ticket: Ticket) => {
    console.log('🎯 Dashboard: handleTabClick called with ticket:', ticket.id, ticket.ticketNumber);
    // Set as active tab ticket and switch to ticket detail view
    setActiveTabTicket(ticket);
    setSelectedTicket(ticket);
    setCurrentView('detail');
  };

  const handleTicketCreate = () => {
    setCurrentView('create');
  };

  const handleProfileUpdate = (updatedUser: User) => {
    updateUser(updatedUser);
  };

  const handleBackToList = () => {
    setCurrentView(user?.role === 'agent' ? 'board' : 'tickets');
    setSelectedTicket(null);
    setActiveTabTicket(null);
    loadTickets(); // Refresh tickets
  };

  // Custom ticket update handler that refreshes both tickets and tabs
  const handleTicketUpdate = async () => {
    console.log('🔄 Dashboard: handleTicketUpdate called - refreshing tickets and tabs');
    try {
      await loadTickets();
    } catch (error) {
      console.error('❌ Error refreshing tickets after update:', error);
      // Don't throw the error - the ticket update itself was successful
    }
    
    // Also refresh tabs immediately
    if (tabBarRef.current) {
      tabBarRef.current.refreshTabs();
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadTickets(false, page, itemsPerPage, savedFilters);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    loadTickets(false, 1, newItemsPerPage, savedFilters);
  };

  const handleRefreshTickets = () => {
    loadTickets();
  };

  // Filter handlers
  const handleFiltersChange = (filters: {
    statusFilter?: string;
    priorityFilter?: string;
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const newFilters = { ...savedFilters, ...filters };
    setSavedFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    loadTickets(false, 1, itemsPerPage, newFilters);
  };

  const toggleSound = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    soundService.setEnabled(newSoundEnabled);
    
    if (newSoundEnabled) {
      // Test sound when enabling
      soundService.playNewTicketSound();
    }
  };

  const testIndividualSound = async (soundType: 'green' | 'yellow' | 'red' | 'update') => {
    switch (soundType) {
      case 'green':
        await soundService.playNewTicketSound();
        break;
      case 'yellow':
        await soundService.playYellowTicketSound();
        break;
      case 'red':
        await soundService.playRedTicketSound();
        break;
      case 'update':
        await soundService.playTicketUpdateSound();
        break;
    }
  };

  // Company match notification handlers
  const handleApproveCompanyMatch = async (matchId: string) => {
    try {
      await apiService.reviewCompanyMatch(matchId, 'approve');
      // Remove notification after successful approval
      setCompanyMatchNotifications(prev => prev.filter(n => n.id !== matchId));
      // Refresh tickets to show updated company assignment
      loadTickets();
    } catch (error) {
      console.error('Error approving company match:', error);
    }
  };

  const handleRejectCompanyMatch = async (matchId: string) => {
    try {
      await apiService.reviewCompanyMatch(matchId, 'reject');
      // Remove notification after successful rejection
      setCompanyMatchNotifications(prev => prev.filter(n => n.id !== matchId));
    } catch (error) {
      console.error('Error rejecting company match:', error);
    }
  };

  const handleDismissCompanyMatch = (matchId: string) => {
    // Just remove the notification without taking action
    setCompanyMatchNotifications(prev => prev.filter(n => n.id !== matchId));
  };

  const renderContent = () => {
    switch (currentView) {
      case 'board':
        return (
          <BoardView
            tickets={tickets}
            loading={loading}
            onTicketSelect={handleTicketSelect}
            onRefresh={handleRefreshTickets}
            onCreateTicket={() => setCurrentView('create')}
            lastUpdated={lastUpdated}
            autoUpdating={autoUpdating}
          />
        );
      case 'create':
        return (
          <CreateTicket 
            onBack={handleBackToList}
            onTicketCreated={handleBackToList}
          />
        );
      case 'detail':
        return selectedTicket ? (
          <TicketDetail 
            ticket={selectedTicket}
            onBack={handleBackToList}
            onTicketUpdate={handleTicketUpdate}
          />
        ) : null;

      case 'users':
        // Check if user has permission to access user management
        if (user?.role === 'agent' && (
          user.permissions?.includes('users.access') || 
          user.permissions?.includes('system.user_management') || 
          user.permissions?.includes('system.management') ||
          user.permissions?.includes('system.role_management') ||
          user.roleName === 'Admin'
        )) {
          return <UserManagement />;
        } else {
          // Redirect to default view if no permission
          setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
          return null;
        }
      case 'audit':
        // Check if user has permission to access audit trail
        if (user?.role === 'agent' && user.permissions?.includes('audit.view')) {
          return <AuditTrail />;
        } else {
          // Redirect to default view if no permission
          setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
          return null;
        }
      case 'insights':
        // Check if user has permission to access insights
        if (user?.role === 'agent' && user.permissions?.includes('insights.view')) {
          return <Insights />;
        } else {
          // Redirect to default view if no permission
          setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
          return null;
        }
      case 'customers':
        // Check if user has permission to access customer management
        if (user?.role === 'agent' && user.permissions?.includes('customers.view')) {
          return <CustomerManagement 
            initialCustomer={selectedCustomer}
            onTicketSelect={async (ticketId: string) => {
              try {
                // Try to find the ticket in the current list first (for performance)
                let ticket = tickets.find(t => t.id === ticketId);
                
                // If not found in current list, fetch it from the API
                if (!ticket) {
                  console.log('Ticket not found in current list, fetching from API:', ticketId);
                  ticket = await apiService.getTicketById(ticketId);
                }
                
                if (ticket) {
                  handleTicketSelect(ticket);
                } else {
                  console.error('Could not find or fetch ticket:', ticketId);
                }
              } catch (error) {
                console.error('Error fetching ticket:', error);
                // Could show a toast notification here
              }
            }} />;
        } else {
          // Redirect to default view if no permission
          setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
          return null;
        }
      case 'devices':
        // Check if user has permission to access device management
        if (user?.role === 'agent' && user.permissions?.includes('devices.view')) {
          return <DeviceManagement 
            initialDevice={selectedDevice}
            onTicketSelect={async (ticketId: string) => {
              try {
                // Try to find the ticket in the current list first (for performance)
                let ticket = tickets.find(t => t.id === ticketId);
                
                // If not found in current list, fetch it from the API
                if (!ticket) {
                  console.log('Ticket not found in current list, fetching from API:', ticketId);
                  ticket = await apiService.getTicketById(ticketId);
                }
                
                if (ticket) {
                  handleTicketSelect(ticket);
                } else {
                  console.error('Could not find or fetch ticket:', ticketId);
                }
              } catch (error) {
                console.error('Error fetching ticket:', error);
                // Could show a toast notification here
              }
            }} />;
        } else {
          // Redirect to default view if no permission
          setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
          return null;
        }
      case 'companies':
        // Check if user has permission to access company management
        if (user?.role === 'agent' && user.permissions?.includes('companies.view')) {
          return <CompanyManagement 
            onTicketSelect={async (ticketId: string) => {
              console.log('🎫 Company onTicketSelect called with ID:', ticketId);
              try {
                // Try to find the ticket in the current list first (for performance)
                let ticket = tickets.find(t => t.id === ticketId);
                console.log('🔍 Found ticket in current list:', ticket ? 'Yes' : 'No');
                
                // If not found in current list, fetch it from the API
                if (!ticket) {
                  console.log('📡 Fetching ticket from API:', ticketId);
                  ticket = await apiService.getTicketById(ticketId);
                  console.log('📥 API response:', ticket);
                }
                
                if (ticket) {
                  console.log('✅ Selecting ticket:', ticket);
                  handleTicketSelect(ticket);
                } else {
                  console.error('❌ Could not find or fetch ticket:', ticketId);
                  // Show user-friendly error
                  alert('Could not load ticket details. Please try again.');
                }
              } catch (error) {
                console.error('💥 Error fetching ticket:', error);
                alert('Error loading ticket: ' + (error instanceof Error ? error.message : 'Unknown error'));
              }
            }}
            onCustomerSelect={(customer: any) => {
              // Switch to customers view and trigger customer selection
              setSelectedCustomer(customer);
              setCurrentView('customers');
            }}
            onDeviceSelect={(device: any) => {
              // Switch to devices view and trigger device selection
              setSelectedDevice(device);
              setCurrentView('devices');
            }}
          />;
        } else {
          // Redirect to default view if no permission
          setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
          return null;
        }
      case 'files':
        // Check if user has permission to access file management
        if (user?.role === 'agent' && user.permissions?.includes('tickets.view_all')) {
          return <FileDashboard />;
        } else {
          // Redirect to default view if no permission
          setCurrentView(user?.role === 'agent' ? 'all-open-tickets' : 'tickets');
          return null;
        }
      case 'account':
        return user ? (
          <MyAccount
            user={user}
            onClose={() => setCurrentView(user?.role === 'agent' ? 'board' : 'tickets')}
            onProfileUpdate={updateUser}
          />
        ) : null;
      case 'service-tickets':
        return (
          <ServiceList
            loading={loading}
            onTicketSelect={handleTicketSelect}
            onRefresh={handleRefreshTickets}
            lastUpdated={lastUpdated}
            autoUpdating={autoUpdating}
            savedFilters={savedFilters}
            onFiltersChange={handleFiltersChange}
          />
        );
      case 'tickets':
      case 'my-tickets':
      case 'my-open-tickets':
      case 'all-open-tickets':
      case 'unassigned':
      case 'resolved':
      default:
        return (
          <TicketList 
            tickets={tickets}
            loading={loading}
            onTicketSelect={handleTicketSelect}
            onRefresh={handleRefreshTickets}
            onCreateTicket={handleTicketCreate}
            currentView={currentView}
            lastUpdated={lastUpdated}
            autoUpdating={autoUpdating}
            onCustomerMessage={handleCustomerMessage}
            onAgentReply={handleAgentReply}
            pagination={paginationData}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            savedFilters={savedFilters}
            onFiltersChange={handleFiltersChange}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800">
                    <Sidebar 
          user={user}
          currentView={currentView}
          onViewChange={(view: string) => {
            // Check permissions before allowing view change
            if (view === 'board' && !(user?.role === 'agent')) {
              return; // Board view is only for agents
            }
            if (view === 'users' && !(user?.role === 'agent' && (
              user.permissions?.includes('users.access') || 
              user.permissions?.includes('system.user_management') || 
              user.permissions?.includes('system.management') ||
              user.permissions?.includes('system.role_management') ||
              user.roleName === 'Admin'
            ))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'audit' && !(user?.role === 'agent' && user.permissions?.includes('audit.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'insights' && !(user?.role === 'agent' && user.permissions?.includes('insights.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'customers' && !(user?.role === 'agent' && user.permissions?.includes('customers.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'devices' && !(user?.role === 'agent' && user.permissions?.includes('devices.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'companies' && !(user?.role === 'agent' && user.permissions?.includes('companies.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'files' && !(user?.role === 'agent' && user.permissions?.includes('tickets.view_all'))) {
              return; // Don't allow view change if no permission
            }
            setCurrentView(view as ViewType);
          }}
          onCreateTicket={handleTicketCreate}
          onLogout={logout}
          onProfileUpdate={handleProfileUpdate}
          isMobile={true}
          onClose={() => setSidebarOpen(false)}
          soundEnabled={soundEnabled}
          onToggleSound={toggleSound}
          onTestSound={testIndividualSound}
        />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden md:flex md:flex-col md:fixed top-16 bottom-0 left-0 z-40 transition-all duration-300 ${sidebarCollapsed ? 'md:w-16' : 'md:w-64'}`}>
        <Sidebar 
          user={user}
          currentView={currentView}
          onViewChange={(view: string) => {
            // Check permissions before allowing view change
            if (view === 'board' && !(user?.role === 'agent')) {
              return; // Board view is only for agents
            }
            if (view === 'users' && !(user?.role === 'agent' && (
              user.permissions?.includes('users.access') || 
              user.permissions?.includes('system.user_management') || 
              user.permissions?.includes('system.management') ||
              user.permissions?.includes('system.role_management') ||
              user.roleName === 'Admin'
            ))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'audit' && !(user?.role === 'agent' && user.permissions?.includes('audit.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'insights' && !(user?.role === 'agent' && user.permissions?.includes('insights.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'customers' && !(user?.role === 'agent' && user.permissions?.includes('customers.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'devices' && !(user?.role === 'agent' && user.permissions?.includes('devices.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'companies' && !(user?.role === 'agent' && user.permissions?.includes('companies.view'))) {
              return; // Don't allow view change if no permission
            }
            if (view === 'files' && !(user?.role === 'agent' && user.permissions?.includes('tickets.view_all'))) {
              return; // Don't allow view change if no permission
            }
            setCurrentView(view as ViewType);
          }}
          onCreateTicket={handleTicketCreate}
          onLogout={logout}
          onProfileUpdate={handleProfileUpdate}
          isMobile={false}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          soundEnabled={soundEnabled}
          onToggleSound={toggleSound}
          onTestSound={testIndividualSound}
        />
      </div>

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${sidebarCollapsed ? 'md:pl-16' : 'md:pl-64'}`}>
        {/* Mobile menu button */}
        <div className="md:hidden sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="p-4 inline-flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Company Match Notifications */}
        {companyMatchNotifications.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 mt-16">
            <div className="px-6 py-4">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">
                Company Match Suggestions ({companyMatchNotifications.length})
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {companyMatchNotifications.map((match) => (
                  <CompanyMatchNotification
                    key={match.id}
                    match={match}
                    onApprove={handleApproveCompanyMatch}
                    onReject={handleRejectCompanyMatch}
                    onDismiss={handleDismissCompanyMatch}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Persistent Tab Bar */}
        <PersistentTabBar
          user={user}
          onTabClick={handleTabClick}
          activeTicketId={activeTabTicket?.id || null}
          ref={tabBarRef}
        />

        {/* Main content area */}
        <main className="flex-1">
          <div className="pt-20 pb-6">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;